import { useState } from "react";
import { Card, Typography, Button, Upload, message } from "antd";
import type { RcFile, UploadFile } from "antd/es/upload/interface";
import * as XLSX from "xlsx";
import { Icon } from "Components";
import { MappingItem, TenantMapping } from "../Mapping";
import { getAccessToken } from "Helpers/api";

const { Title, Paragraph } = Typography;

interface MappingWizardStep2Props {
  tenantInfo: TenantMapping;
  onNext: (file: File, excel: MappingItem[]) => void;
  onBack: () => void;
}

export function MappingWizardStep2({ tenantInfo, onNext, onBack }: MappingWizardStep2Props) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [excelData, setExcelData] = useState<any>();
  const handleDownloadTemplate = async () => {
    try {
      const token = getAccessToken();
      const formData = new FormData();
      formData.append("callfor", "report");
      formData.append("calltype", "PCI_Properties");
      formData.append(
        "callparams",
        JSON.stringify({
          reportType: "PCI_Compliance",
          groupId: Number(tenantInfo.id),
          fileType: "xlsx",
        })
      );

      const urlhost = window.location.host;
      let APIurl = 'https://api-dev.venzagrc.com';
      if (urlhost.includes('tg.')) {
        APIurl = 'https://api-stage.venzagrc.com';
      } else if(urlhost.includes("localhost")) {
        APIurl = 'https://api-dev.venzagrc.com';
      } else if(!urlhost.includes('ev.') && !urlhost.includes('tg.')) {
        APIurl = 'https://api-prod.venzagrc.com';
      }
      const response = await fetch(`${APIurl}/?awsToken=${token}`, {
        method: "POST",
        body: formData,
        headers: {
          "Accept": "application/json", 
        },
      });

      if (!response.ok) {
        message.error("Failed to generate template");
        return;
      }

      const data = await response.json();

      if (!data?.url) {
        message.error("Invalid download URL");
        return;
      }

      const link = document.createElement("a");
      link.href = data.url;
      link.download = `${tenantInfo.name}_hierarchy_template.xlsx`;
      link.click();
      link.remove();
      message.success("Template downloaded successfully");

    } catch (err) {
      console.error(err);
      message.error("Error downloading template");
    }
  };

  const handleBeforeUpload = (file: File) => {
    setUploadedFile(file);
    const reader = new FileReader();

    reader.onload = (e) => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const rows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1 });

      const parsedData = rows.slice(1).map((row: any[]) => ({
        tenantName: row[0] ?? "",
        propertyName: row[1] ?? "",
        encytroMid: row[2] ?? "",
        encytroMName: row[3] ?? "",
      }));

      setExcelData(parsedData.map((el, index) => {
        return {
          id: String(index + 1),
          venzaProperty: el.propertyName,
          venzaPropertyId: "",
          encytroOffice: el.encytroMName,
          encytroMid: el.encytroMid,
          status: (!!el.propertyName && !!el.encytroMid) ? "matched" : "needs review",
        }
      }));
    };

    reader.readAsBinaryString(file);
    return false;
  };



  const handleContinue = () => {
    if (uploadedFile) {
      onNext(uploadedFile, excelData);
    } else {
      message.error("Please upload a file first");
    }
  };

  const rcFile = uploadedFile as RcFile;

  const fileList: UploadFile<unknown>[] = uploadedFile
    ? [
      {
        uid: "1",
        name: rcFile.name,
        status: "done",
        size: rcFile.size,
        originFileObj: rcFile,
        type: rcFile.type,
      },
    ]
    : [];

  return (
    <Card className="mvh-80">
      <div className="mbx-16">
        <Title level={3} className="headerTitle">Upload Tenant Hierarchy</Title>
        <p className="headerSubtitle">
          Download the template, complete it with property information, and re-upload it.
        </p>
      </div>

      <div className="tenantInfo uploadTenantInfo">
        <Icon type="tenant" className="card-foreground" />
        <div>
          <strong>Client:</strong>
          <span>{tenantInfo.name}</span>
        </div>
      </div>

      {/* Step 1: Download Template */}
      <div className="mbx-16">
        <Title level={5} className="fw-400 card-foreground">Step 1: Download Template</Title>
        <Button className="downloadExcelTemplate" icon={
          <Icon type="excel" />
        } onClick={handleDownloadTemplate}>
          Download Excel Template
        </Button>
        <Paragraph className="stepDescription">
          Fill in your tenant property information, including merchant identifiers.
        </Paragraph>
      </div>

      {/* Step 2: Upload */}
      <div className="mbx-16">
        <Title level={5} className="fw-400 card-foreground">Step 2: Upload Completed Template</Title>
        <Upload.Dragger
          className={uploadedFile ? "no-hover" : ""}
          accept=".xlsx,.xls"
          multiple={false}
          beforeUpload={handleBeforeUpload}
          fileList={fileList}
          style={{ padding: "2rem", textAlign: "center", backgroundColor: !uploadedFile ? '#fff' : "#0302130d", borderRadius: 10, borderWidth: 2, borderColor: !uploadedFile ? "inherit" : "#030213", height: "14rem" }}
        >
          {uploadedFile ? (
            <div className="uploadedFile">
              <Icon type="uploadFile" className="mbx-12" />
              <p className="uploadedFileName">{uploadedFile.name}</p>
              <Paragraph className="uploadedFileSize mbx-12" >{(uploadedFile.size / 1024).toFixed(2)} KB</Paragraph>
              <Button className="browseFiles" onClick={() => setUploadedFile(null)}>Remove File</Button>
            </div>
          ) : (
            <div className="uploadPlaceholder">
              <Icon type="uploadPlaceholder" className="text-muted-foreground" />
              <p className="uploadText">Drag and drop your file here, or</p>
              <Button className="browseFiles">Browse Files</Button>
              <Paragraph className="uploadFormats">Accepted formats: .xlsx, .xls</Paragraph>
            </div>
          )}
        </Upload.Dragger>
      </div>

      {/* Buttons */}
      <div className="footerButtons">
        <Button className="backButton" onClick={onBack}>Back</Button>
        <Button className="confirmButtonWizard" type="primary" onClick={handleContinue} disabled={!uploadedFile}>Continue to Review</Button>
      </div>
    </Card>

  );
}
